import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import {
  RegisterService,
  TRegisterEquipmentFormGroup,
} from 'src/app/core/services/register/register.service';
import { BusinessEquipmentFormObject } from 'src/app/shared/models/business-equipment/business-equipment-form-object/business-equipment-form-object';
import { BusinessSummaryResponseDTO } from 'src/app/shared/models/dtos/responses/business-summary-response-dto/business-summary-response-dto';

export type TBusinessEquipmentFormPhase =
  | 'EPSON_UNREGISTERED_PENDING'
  | 'LNR_REGISTER';

@Component({
  selector: 'app-business-equipment-form',
  templateUrl: './business-equipment-form.component.html',
  styleUrls: ['./business-equipment-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Business equipment section of user register
 */
export class BusinessEquipmentFormComponent implements OnInit, OnDestroy {
  @Input() equipmentData!: BusinessEquipmentFormObject;
  @Input() registerEquipmentFormGroup!: TRegisterEquipmentFormGroup;
  @Input() feedback?: 'registered' | 'eligible' | '';
  @Input() businessSummary!: BusinessSummaryResponseDTO;
  @Input() loading = false;
  @Input() showAddEquipmentButton = true;
  @Output() deleteEquipmentAction = new EventEmitter<FormGroup>();
  private readonly subsink = new Subscription();
  private _phase: TBusinessEquipmentFormPhase = 'LNR_REGISTER';
  businessEquipmentFormGroup!: UntypedFormGroup;
  formArray!: UntypedFormArray;
  regionOptions?: any;
  today = new Date().toISOString();
  canAddEquipment: boolean = true;
  isAdmin: boolean = false;
  isCallCenterAgent: boolean = false;

  /**
   * @return {TBusinessEquipmentFormPhase}
   */
  @Input()
  get phase(): TBusinessEquipmentFormPhase {
    return this._phase;
  }

  /**
   * @param {TBusinessEquipmentFormPhase} value
   */
  set phase(value: TBusinessEquipmentFormPhase) {
    this._phase = value;
    if (this.pendingProductRegistration && this.formArray) {
      this.formArray.disable();
    }
  }

  /**
   * Equipment form getter
   */
  get equipmentsFormGroup() {
    return this.businessEquipmentFormGroup.get(
      'equipments'
    ) as UntypedFormArray;
  }

  /**
   * Uses formBuilder
   * @constructor
   */
  constructor(
    private fb: UntypedFormBuilder,
    private registerService: RegisterService,
    private authenticatedUserService: AuthenticatedUserService
  ) {}

  /**
   * Init form
   */
  ngOnInit(): void {
    this.isAdmin =
      this.authenticatedUserService.isGlobalAdmin() ||
      this.authenticatedUserService.isRegionalAdmin();
    this.isCallCenterAgent = this.authenticatedUserService.isCallCenterAgent();
    this.businessEquipmentFormGroup = this.equipmentData.formGroup;
    this.formArray = this.businessEquipmentFormGroup.get(
      'equipments'
    ) as UntypedFormArray;
    this.feedback = '';
    this.canAddEquipment = !(!this.loading && this.phase === 'LNR_REGISTER');
  }

  /**
   * Cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Removing the last fields index from a form array
   * @param {number | FormGroup} param - Index or form control to remove
   */
  removeTuple(param?: number | FormGroup) {
    if (typeof param === 'number') {
      let removeIndex = this.equipmentsFormGroup.length - 1;
      if (param !== null) {
        removeIndex = param;
      }
      this.formArray.removeAt(removeIndex);
    }
    if (param instanceof FormGroup) {
      this.deleteEquipmentAction.emit(param);
    }
  }

  /**
   * Getting the fields from the form group
   * @param {number} index
   * @return {FormGroup} index
   */
  getTuplesFormGroup(index: number): UntypedFormGroup {
    return this.formArray.at(index) as UntypedFormGroup;
  }

  /**
   * @param {number} index
   * @param {string} controlName
   * @return {FormControl}
   */
  getControl(index: number, controlName: string): FormControl {
    return this.getTuplesFormGroup(index).get(controlName) as FormControl;
  }

  /**
   * Whether there is a pending equipment to be registered
   */
  get pendingProductRegistration() {
    return this.phase === 'EPSON_UNREGISTERED_PENDING';
  }

  /**
   * Decides which label feedback to use
   */
  get feedbackLabel() {
    let feedbackLabel = '';
    if (this.feedback === 'eligible') {
      feedbackLabel = 'product-eligible-for-rewards-program';
    } else if (this.feedback === 'registered') {
      feedbackLabel = 'product-successfully-registered';
    }
    return feedbackLabel;
  }

  /**
   * Gets controls as formGroup
   */
  get equipmentFormGroups(): FormGroup[] {
    return this.equipmentsFormGroup.controls as FormGroup[];
  }
}
